import React from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Grid, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as MuiIcons from '@mui/icons-material';

const getIcon = (iconName) => {
  if (!iconName) return null;
  const Icon = MuiIcons[iconName] || MuiIcons['ConfirmationNumber'];
  return <Icon sx={{ fontSize: 26 }} />;
};

const formatPrice = (item) => {
  if (item.priceLabel) return item.priceLabel;
  if (item.price === 0) return 'Free';
  return `${item.currency || 'PHP'} ${Number(item.price).toLocaleString()}`;
};

/**
 * InlineSelectionGroup
 * --------------------
 * Renders a `selection_group` field type embedded inside a fields step.
 * Mirrors SelectionGroup.js but reads config from `field` instead of `group`.
 *
 * Props:
 *   field   — the field schema object (type: 'selection_group')
 *   value   — current value (string | string[])
 *   onChange — (fieldId, value) => void
 *   error   — validation error string
 */
const InlineSelectionGroup = ({ field, value, onChange, error }) => {
  const theme = useTheme();
  const {
    id,
    label,
    subtitle,
    badge,
    selectionMode = 'single',
    items = [],
    columns = 3,
  } = field;

  const selected = Array.isArray(value) ? value : value ? [value] : [];

  const isSelected = (itemId) => selected.includes(itemId);

  const handleSelect = (itemId) => {
    if (!field.disabled) {
      if (selectionMode === 'multi') {
        const next = isSelected(itemId)
          ? selected.filter((s) => s !== itemId)
          : [...selected, itemId];
        onChange(id, next);
      } else {
        // single: toggle off if already selected
        onChange(id, isSelected(itemId) ? null : itemId);
      }
    }
  };

  // Clamp columns to number of items
  const effectiveCols = Math.min(columns, items.length || 1);
  const colSize = Math.floor(12 / effectiveCols);

  return (
    <Box sx={{ mb: 1 }}>
      {/* Header — mirrors section header style */}
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {badge && (
            <Box
              sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: 'rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {badge}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {label}
              {field.required && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
              )}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Card grid */}
      <Grid container spacing={2}>
        {items.map((item) => {
          const sel = isSelected(item.id);
          const unavailable = item.available === false;

          return (
            <Grid item xs={12} sm={colSize} key={item.id}>
              <Card
                onClick={() => !unavailable && handleSelect(item.id)}
                sx={{
                  cursor: unavailable ? 'not-allowed' : 'pointer',
                  border: sel
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  // outline: sel ? 'none' : `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.18s ease',
                  opacity: unavailable ? 0.5 : 1,
                  position: 'relative',
                  height: '100%',
                  '&:hover': !unavailable ? {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
                  } : {},
                }}
                elevation={sel ? 3 : 1}
              >
                {/* Selected checkmark */}
                {sel && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute', top: 10, right: 10,
                      color: theme.palette.primary.main, fontSize: 22,
                    }}
                  />
                )}

                {/* Badge chip */}
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color="primary"
                    sx={{ position: 'absolute', top: 10, right: sel ? 38 : 10, fontSize: 10 }}
                  />
                )}

                <CardContent sx={{ pb: '16px !important' }}>
                  {/* Item image */}
                  {item.image && (
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1, mb: 1.5 }}
                    />
                  )}

                  {/* Icon (only if no image) */}
                  {!item.image && item.icon && (
                    <Box
                      sx={{
                        mb: 1.5, color: sel ? theme.palette.primary.main : 'text.secondary',
                        transition: 'color 0.2s',
                      }}
                    >
                      {getIcon(item.icon)}
                    </Box>
                  )}

                  {/* Title */}
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {item.title}
                  </Typography>

                  {/* Description */}
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 36 }}>
                      {item.description}
                    </Typography>
                  )}

                  {/* Price */}
                  {item.price !== undefined && item.price !== null && (
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={item.price === 0 ? 'success.main' : 'primary.main'}
                    >
                      {formatPrice(item)}
                    </Typography>
                  )}

                  {/* Capacity chip */}
                  {item.capacity !== undefined && item.capacity !== null && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {item.capacity} slot{item.capacity !== 1 ? 's' : ''} available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default InlineSelectionGroup;
